import { RESERVED_USERNAMES, whatsappNumber } from "./menu.js";

// Controles de los datos del negocio (alta y "Mi negocio"). Los mismos límites están en
// firestore.rules (businessOk), que es lo que de verdad frena a quien escriba directo a
// Firestore sin pasar por el panel. Si cambiás uno, cambiá el otro.
//
// React ya muestra todo como texto (nunca como HTML), así que un "<script>" cargado en el
// nombre no se ejecuta. Igual no aceptamos < ni >: no hacen falta en ningún dato del
// negocio y así no queda nada raro guardado que otra herramienta pueda interpretar.

export const BUSINESS_LIMITS = {
    nombreEmpresa: { min: 2, max: 60, label: "El nombre del negocio" },
    usuarioEmpresa: { min: 3, max: 30, label: "El nombre de usuario" },
    descripcionEmpresa: { min: 10, max: 350, label: "La descripción" },
    calle: { min: 2, max: 80, label: "La calle" },
    altura: { min: 1, max: 10, label: "El número de la dirección" },
    ciudad: { min: 2, max: 60, label: "La ciudad" },
    provincia: { min: 2, max: 40, label: "La provincia" },
    telefono: { min: 11, max: 15, label: "El WhatsApp" },
    horario: { min: 3, max: 80, label: "El horario principal" },
    horarioSec: { min: 0, max: 80, label: "El otro horario" }
};

export const PROVINCIAS = [
    "Buenos Aires", "CABA", "Catamarca", "Chaco", "Chubut", "Córdoba", "Corrientes", "Entre Ríos",
    "Formosa", "Jujuy", "La Pampa", "La Rioja", "Mendoza", "Misiones", "Neuquén", "Río Negro",
    "Salta", "San Juan", "San Luis", "Santa Cruz", "Santa Fe", "Santiago del Estero",
    "Tierra del Fuego", "Tucumán"
];

// Caracteres de control (salvo el salto de línea, que se maneja aparte).
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0009\u000b-\u001f\u007f-\u009f\u200b-\u200f\u2028\u2029\u202a-\u202e\u2066-\u2069\ufeff]/g;
const ANGLE = /[<>]/;
const LETTER = /\p{L}/u;

// Una sola línea: sin saltos, sin espacios repetidos y sin espacios en las puntas.
export const cleanLine = (value) => String(value ?? "").replace(/[\r\n]+/g, " ").replace(CONTROL, "").replace(/\s+/g, " ").trim();

// La descripción puede tener saltos de línea, pero como mucho una línea en blanco seguida.
export const cleanParagraph = (value) => String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .replace(CONTROL, "")
    .split("\n").map((line) => line.replace(/\s+/g, " ").trim()).join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

// Para textos sueltos (productos, categorías): devuelve el error o null.
export const textProblem = (label, value, max) => {
    if (!value) return `${label} no puede quedar vacío.`;
    if (ANGLE.test(value)) return `${label} no puede tener los signos < ni >.`;
    if (value.length > max) return `${label} es muy largo (máximo ${max} caracteres).`;
    return null;
}

// Colores del menú: siempre #rrggbb, como los da el selector de color.
export const COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

// Usuario del link del menú: minúsculas, números y guiones sueltos (no al principio ni al final).
export const USERNAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// Número de la dirección: 1234, 1234B, S/N o Km 12.
export const ALTURA_PATTERN = /^([0-9]{1,6} ?[A-Za-z]?|[Ss]\/[Nn]|[Kk][Mm] ?[0-9]{1,4}([.,][0-9]{1,3})?)$/;

// Lo que se puede escribir en el WhatsApp antes de quedarnos con los dígitos.
const PHONE_CHARS = /^[0-9+\-\s().]*$/;

// Devuelve el número listo para wa.me (solo dígitos, con código de país) o un mensaje de error.
export const checkPhone = (value) => {
    const raw = cleanLine(value);
    if (!raw) return { error: "Cargá el WhatsApp del negocio." };
    if (!PHONE_CHARS.test(raw)) return { error: "El WhatsApp solo puede tener números (y si querés +, espacios o guiones)." };

    const number = whatsappNumber(raw);
    // Si empieza con 0 es un número argentino escrito como se marca acá: sin el 0
    // tienen que quedar los 10 dígitos de área + número (si quedan más, tiene el 15).
    const local = raw.replace(/\D/g, "").startsWith("0");
    if (number.startsWith("54") || local) {
        if (number.length !== 13 || !number.startsWith("549")) {
            return { error: "Revisá el WhatsApp: tiene que ser el código de área y el número, sin 0 ni 15. Por ejemplo 341 555-1234." };
        }
        return { value: number };
    }
    if (number.length < BUSINESS_LIMITS.telefono.min || number.length > BUSINESS_LIMITS.telefono.max) {
        return { error: "Revisá el WhatsApp: tiene que ser el código de área y el número, sin 0 ni 15. Por ejemplo 341 555-1234." };
    }
    return { value: number };
}

const checkText = (key, value, { required = true, letters = false } = {}) => {
    const { min, max, label } = BUSINESS_LIMITS[key];
    if (!value) return required ? `${label} no puede quedar vacío.` : null;
    if (ANGLE.test(value)) return `${label} no puede tener los signos < ni >.`;
    if (value.length < min) return `${label} es muy corto (mínimo ${min} caracteres).`;
    if (value.length > max) return `${label} es muy largo (máximo ${max} caracteres).`;
    if (letters && !LETTER.test(value)) return `${label} tiene que tener letras.`;
    return null;
}

// Limpia y controla los datos del formulario. Devuelve { data } con los datos listos
// para guardar, o { errors } con un mensaje por campo.
export const validateBusiness = (fields, { isNew = false } = {}) => {
    const data = {
        nombreEmpresa: cleanLine(fields.nombreEmpresa),
        descripcionEmpresa: cleanParagraph(fields.descripcionEmpresa),
        calle: cleanLine(fields.calle),
        altura: cleanLine(fields.altura),
        ciudad: cleanLine(fields.ciudad),
        provincia: cleanLine(fields.provincia),
        horario: cleanLine(fields.horario),
        horarioSec: cleanLine(fields.horarioSec)
    };
    const errors = {};
    const add = (key, error) => { if (error) errors[key] = error; };

    add("nombreEmpresa", checkText("nombreEmpresa", data.nombreEmpresa, { letters: true }));
    add("descripcionEmpresa", checkText("descripcionEmpresa", data.descripcionEmpresa, { letters: true }));
    add("calle", checkText("calle", data.calle, { letters: true }));
    add("altura", checkText("altura", data.altura) || (ALTURA_PATTERN.test(data.altura) ? null : "Poné solo el número de la dirección, por ejemplo 1234 (o S/N si no tiene)."));
    add("ciudad", checkText("ciudad", data.ciudad, { letters: true }));
    add("provincia", checkText("provincia", data.provincia, { letters: true }));
    add("horario", checkText("horario", data.horario));
    add("horarioSec", checkText("horarioSec", data.horarioSec, { required: false }));

    const phone = checkPhone(fields.telefono);
    if (phone.error) errors.telefono = phone.error;
    else data.telefono = phone.value;

    if (isNew) {
        data.usuarioEmpresa = String(fields.usuarioEmpresa || "");
        const { min, max } = BUSINESS_LIMITS.usuarioEmpresa;
        if (data.usuarioEmpresa.length < min || data.usuarioEmpresa.length > max) {
            errors.usuarioEmpresa = `El nombre de usuario tiene que tener entre ${min} y ${max} caracteres.`;
        } else if (!USERNAME_PATTERN.test(data.usuarioEmpresa)) {
            errors.usuarioEmpresa = "El nombre de usuario no puede empezar ni terminar con guion, ni tener dos guiones seguidos.";
        } else if (RESERVED_USERNAMES.includes(data.usuarioEmpresa)) {
            errors.usuarioEmpresa = "Ese nombre de usuario no está disponible. Probá con otro.";
        }
    }

    return Object.keys(errors).length ? { errors, data } : { data };
}

// Costo del envío a domicilio (campo `costoEnvio`, público): null = el negocio lo confirma
// por WhatsApp (el carrito avisa que el total no lo incluye), 0 = gratis, y si no el monto
// fijo que el carrito suma al total. firestore.rules (businessOk) tiene el mismo tope.
export const MAX_DELIVERY_COST = 9999999;

// Lee lo que escribe el dueño ("1500", "1.500", "1500,50") y devuelve { value } o { error }.
export const checkDeliveryCost = (text) => {
    const raw = cleanLine(text).replace(/^\$\s*/, "");
    if (!raw) return { error: "Escribí cuánto cobrás el envío." };
    // Puntos de miles (1.500 o 1.500.000) y coma o punto para los centavos.
    const plain = /^[0-9]{1,3}(\.[0-9]{3})+(,[0-9]{1,2})?$/.test(raw) ? raw.replace(/\./g, "").replace(",", ".") : raw.replace(",", ".");
    if (!/^[0-9]{1,7}(\.[0-9]{1,2})?$/.test(plain)) return { error: "Revisá el costo: tiene que ser un número, por ejemplo 1500." };
    const value = Number(plain);
    if (value > MAX_DELIVERY_COST) return { error: "El costo del envío es demasiado alto." };
    return { value };
}

// El costo guardado, o null si no hay uno válido (a confirmar).
export const deliveryCostOf = (business) => {
    const n = business?.costoEnvio;
    return typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= MAX_DELIVERY_COST ? n : null;
}
